import classNames from 'classnames';

import { UploadcareImage } from '@/components/UploadcareImage';
import type { ContentBlock, Media as MediaType } from '@/types';
import { Media as SectionMedia } from '@/ui/Section/Media/Media';

import { Video } from '../Video/Video';

import { PlayCircleFilled } from './PlayCircleFilled';

import styles from './Content.module.scss';

interface Props {
    hasMedia: ContentBlock['has_media'];
    hasVideo: ContentBlock['has_video'];
    media?: ContentBlock['media'];
    title: ContentBlock['title'];
    video: ContentBlock['video'];
}

export function Media({ hasMedia, hasVideo, media, title, video }: Props) {
    const Element = hasVideo && video ? 'a' : 'div';
    const contentHasMedia =
        (media && hasMedia && isUsableMediaFile(media.desktop_file)) || (hasVideo && video);

    if (!contentHasMedia) {
        return null;
    }

    return (
        <Element
            className={classNames(styles.multimedia, {
                [styles.shadow]: media?.shadow,
            })}
            href={hasVideo && video ? `#video-${video.videoId}-${video.type}` : undefined}
        >
            {hasMedia && isUsableMediaFile(media?.desktop_file) && (
                <MediaObject
                    file={media.desktop_file}
                    title={title}
                    className={classNames({
                        [styles.hiddenOnMobile]: isUsableMediaFile(media.mobile_file),
                    })}
                />
            )}
            {hasVideo && video && (
                <div id="video">
                    <Video
                        className={classNames(styles.video, {
                            [styles.hasImage]: Boolean(hasMedia && video),
                        })}
                        video={video}
                    />
                </div>
            )}
            {hasMedia && isUsableMediaFile(media?.mobile_file) && (
                <MediaObject
                    file={media.mobile_file}
                    title={title}
                    className={styles.visibleOnMobile}
                />
            )}
            {hasVideo && media && video && (
                <div className={styles.playIconWrapper}>
                    <PlayCircleFilled className={styles.playIcon} />
                    <span className={styles.iconLabel}>Watch video</span>
                </div>
            )}
        </Element>
    );
}

function MediaObject(props: {
    className?: string;
    file: MediaType & { url: string };
    title?: string;
}) {
    const { className, file, title } = props;

    if (file.mimeType?.startsWith('image')) {
        return (
            <UploadcareImage
                className={classNames(styles.image, className)}
                alt={title ?? ''}
                height={file.height ?? 1200}
                src={file.url}
                width={file.width ?? 800}
            />
        );
    }

    if (file.mimeType?.startsWith('video')) {
        return (
            <SectionMedia
                alt={title ?? ''}
                className={classNames(styles.video, className)}
                src={file.url}
                type="video"
            />
        );
    }

    console.warn(
        `Unsupported media type given to <MediaObject> component of <Content> block: "${file.mimeType}".`,
    );

    return null;
}

function isUsableMediaFile(
    file: MediaType | null | undefined,
): file is MediaType & { url: string } {
    return typeof file === 'object' && file !== null && file.url !== undefined;
}
